import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import "../../assets/styles/category.css";
import { MdOutlineProductionQuantityLimits, MdDragIndicator } from "react-icons/md";
import { FaBoxOpen } from "react-icons/fa";
import { BiCategoryAlt } from "react-icons/bi";
import { IoBagAdd } from "react-icons/io5";
import { TbMacro } from "react-icons/tb";
import {MdDelete } from "react-icons/md";
import { RiDeleteBin5Line } from "react-icons/ri";
import Toggle from "../../components/common/toggle";
import { ReactSortable } from "react-sortablejs";

// Mock data for categories
const initialCategoriesData = [
  {
    id: "macro-family-1",
    name: "Macro Family 1",
    icon: (
      <span className="category-icon-bg">
        <TbMacro size={24} color="#fff" />
      </span>
    ),
    children: [
      {
        id: "macro-family-1-category",
        name: "Category",
        icon: (
          <span className="category-icon-bg">
            <BiCategoryAlt size={24} color="#fff" />
          </span>
        ),
        children: [
          {
            id: "macro-family-1-product-1",
            name: "Product 1",
            icon: (
              <span className="category-icon-bg">
                <MdOutlineProductionQuantityLimits size={24} color="#fff" />
              </span>
            ),
          },
          {
            id: "macro-family-1-product-2",
            name: "Product 2",
            icon: (
              <span className="category-icon-bg">
                <FaBoxOpen size={24} color="#fff" />
              </span>
            ),
          },
        ],
      },
    ],
  },
  {
    id: "macro-family-2",
    name: "Macro Family 2",
    icon: (
      <span className="category-icon-bg">
        <TbMacro size={24} color="#fff" />
      </span>
    ),
    children: [
      {
        id: "macro-family-2-category",
        name: "Category",
        icon: (
          <span className="category-icon-bg">
            <BiCategoryAlt size={24} color="#fff" />
          </span>
        ),
        children: [
          {
            id: "macro-family-2-product-1",
            name: "Product 1",
            icon: (
              <span className="category-icon-bg">
                <MdOutlineProductionQuantityLimits size={24} color="#fff" />
              </span>
            ),
          },
          {
            id: "macro-family-2-product-2",
            name: "Product 2",
            icon: (
              <span className="category-icon-bg">
                <FaBoxOpen size={24} color="#fff" />
              </span>
            ),
          },
        ],
      },
    ],
  },
  {
    id: "macro-family-3",
    name: "Macro Family 3",
    icon: (
      <span className="category-icon-bg">
        <TbMacro size={24} color="#fff" />
      </span>
    ),
    children: [
      {
        id: "macro-family-3-category",
        name: "Category",
        icon: (
          <span className="category-icon-bg">
            <BiCategoryAlt size={24} color="#fff" />
          </span>
        ),
        children: [
          {
            id: "macro-family-3-product-1",
            name: "Product 1",
            icon: (
              <span className="category-icon-bg">
                <MdOutlineProductionQuantityLimits size={24} color="#fff" />
              </span>
            ),
          },
          {
            id: "macro-family-3-product-2",
            name: "Product 2",
            icon: (
              <span className="category-icon-bg">
                <FaBoxOpen size={24} color="#fff" />
              </span>
            ),
          },
        ],
      },
    ],
  },
];
// Recursive CategoryItem for 3-level dropdown
const CategoryItem = ({
  category,
  expanded,
  onToggle,
  isChild,
  isLastChild,
  menuOpen,
  onMenuClick,
  menuId,
  parentName,
  level = 0,
  expandedMap,
  setExpandedMap,
  openMenuId,
  setOpenMenuId,
  checkedMap,
  onToggleChange,
  onChildrenUpdate,
}) => {
  const hasChildren = category.children && category.children.length > 0;
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggle = () => {
    setExpandedMap((prev) => {
      if (level === 0) {
        // Toggle the clicked parent, close others if opening, or all closed if toggling off
        const isOpen = !!prev[menuId];
        if (isOpen) {
          // Close it (all closed)
          return {};
        } else {
          // Open only this one
          const newMap = {};
          newMap[menuId] = true;
          return newMap;
        }
      } else {
        // For children, just toggle as before
        return { ...prev, [menuId]: !prev[menuId] };
      }
    });
    if (onToggle) onToggle();
  };

  const handleChildrenSort = (newChildren) => {
    if (onChildrenUpdate) {
      onChildrenUpdate(newChildren);
    }
  };

  // If this is a product (leaf node), clicking the name navigates to detail page
  const handleProductClick = (e) => {
    e.stopPropagation();
    navigate(`/products/${encodeURIComponent(category.name)}`);
  };
  return (
    <>
      <div
        className={`category-item${
          isChild ? " category-item-child" : ""
        } level-${level}`}
        style={{
          display: "flex",
          alignItems: "center",
          paddingLeft: isMobile ? `${level * 12 + 8}px` : `${level * 24 + 12}px`,
          minHeight: isMobile ? "40px" : "44px",
          background:
            menuId === "Macro Family 1" || menuOpen ? "#f7faff" : "transparent",
          position: "relative",
        }}
      >
        {isChild && (
          <span
            className={`category-tree-vert${
              isLastChild ? " category-tree-vert-last" : ""
            }`}
            style={{
              position: "absolute",
              left: isMobile ? `${(level - 1) * 12 + 2}px` : `${(level - 1) * 24 + 2}px`,
              height: "100%",
            }}
          />
        )}
        {isChild && (
          <span
            className="category-tree-horiz"
            style={{ left: isMobile ? `${level * 12 - 6}px` : `${level * 24 - 10}px` }}
          />
        )}
        
        {/* Drag Handle */}
        <MdDragIndicator 
          className="drag-handle" 
          size={isMobile ? 18 : 20} 
          color="#898989" 
          style={{ cursor: 'grab', marginRight: isMobile ? 4 : 8, marginLeft: 4 }} 
        />
        
        <span
          className="category-content"
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            cursor:
              (level === 0 || level === 1) && hasChildren
                ? "pointer"
                : "default",
          }}
          onClick={
            (level === 0 || level === 1) && hasChildren
              ? handleToggle
              : undefined
          }
        >
          {hasChildren ? (
            <span
              onClick={(e) => {
                if (level !== 0 && level !== 1) {
                  e.stopPropagation();
                  handleToggle();
                }
              }}
              className="category-toggle"
              style={{ cursor: "pointer", marginRight: isMobile ? 4 : 8 }}
            >
              {expandedMap[menuId] ? (
                <ChevronDownIcon width={isMobile ? 16 : 20} height={isMobile ? 16 : 20} color="#0A4DA2" />
              ) : (
                <ChevronRightIcon width={isMobile ? 16 : 20} height={isMobile ? 16 : 20} color="#0A4DA2" />
              )}
            </span>
          ) : (
            <span
              className="category-toggle-placeholder"
              style={{ width: isMobile ? 16 : 20, display: "inline-block", marginRight: isMobile ? 4 : 8 }}
            />
          )}
          <span className="category-icon" style={{ marginRight: isMobile ? 6 : 10 }}>
            {category.icon}
          </span>
          {hasChildren ? (
            <span
              className="category-name"
              style={{
                flex: 1,
                fontSize: isMobile ? "14px" : "16px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {category.name}
            </span>
          ) : (
            <span
              className="category-name"
              style={{
                fontWeight: 500,
                color: "#0A4DA2",
                flex: 1,
                cursor: "pointer",
                fontSize: isMobile ? "14px" : "16px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              onClick={handleProductClick}
            >
              {category.name}
            </span>
          )}
          <div
            className="downArrow"
            style={{ display: "flex", alignItems: "center" }}
          >
            {/* {level !== 2 && <FaCaretDown onClick={handleToggle} color="#898989" style={{ cursor: 'pointer', marginRight: 8 }} />} */}
            {level === 2 && (
              <>
                <Toggle 
                  onChange={(newChecked) => onToggleChange(menuId, newChecked)} 
                  checked={checkedMap[menuId] || false} 
                />
                <MdDelete
                  style={{ marginLeft: isMobile ? "6px" : "10px", cursor: "pointer", flexShrink: 0 }}
                  color="#da2929"
                  size={isMobile ? 18 : 20}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle delete action here
                    if (
                      window.confirm(
                        `Are you sure you want to delete "${category.name}"?`
                      )
                    ) {
                      console.log("Deleting:", category.name);
                      // Add your delete logic here
                    }
                  }}
                />
              </>
            )}
          </div>
        </span>
      </div>
      {/* Render children recursively if expanded */}
      {hasChildren && expandedMap[menuId] && (
        <ReactSortable
          list={category.children}
          setList={handleChildrenSort}
          handle=".drag-handle"
          animation={200}
          ghostClass="sortable-ghost"
          chosenClass="sortable-chosen"
          dragClass="sortable-drag"
          style={{ listStyle: "none", padding: 0, margin: 0 }}
        >
          {category.children.map((child, idx) => (
            <div key={child.id}>
              <CategoryItem
                category={{ ...child, icon: child.icon || category.icon }}
                isChild={true}
                isLastChild={idx === category.children.length - 1}
                menuOpen={openMenuId === `${menuId}-${child.name}`}
                onMenuClick={onMenuClick}
                menuId={`${menuId}-${child.name}`}
                parentName={category.name}
                level={level + 1}
                expandedMap={expandedMap}
                setExpandedMap={setExpandedMap}
                openMenuId={openMenuId}
                setOpenMenuId={setOpenMenuId}
                checkedMap={checkedMap}
                onToggleChange={onToggleChange}
                onChildrenUpdate={(newChildren) => {
                  if (onChildrenUpdate) {
                    onChildrenUpdate(category.children.map((c, i) => 
                      i === idx ? { ...c, children: newChildren } : c
                    ));
                  }
                }}
              />
            </div>
          ))}
        </ReactSortable>
      )}
    </>
  );
};

const Products = ({checked: checkedProp, onChange: onChangeProp}) => {
  const [categoriesData, setCategoriesData] = useState(initialCategoriesData);
  // No list is forced open; user can open/close any
  const [expandedMap, setExpandedMap] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  const [checkedMap, setCheckedMap] = useState({});

  const handleToggleChange = (menuId, newChecked) => {
    setCheckedMap(prev => ({ ...prev, [menuId]: newChecked }));
    if (onChangeProp) {
      onChangeProp(menuId, newChecked);
    }
  };

  const handleRootSort = (newList) => {
    setCategoriesData(newList);
  };

  const updateCategoryChildren = (categoryId, newChildren) => {
    setCategoriesData(prevData => {
      const updateChildren = (items) => {
        return items.map(item => {
          if (item.id === categoryId) {
            return { ...item, children: newChildren };
          }
          if (item.children) {
            return { ...item, children: updateChildren(item.children) };
          }
          return item;
        });
      };
      return updateChildren(prevData);
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (openMenuId === null) return;
    const handleClick = () => setOpenMenuId(null);
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [openMenuId]);

  return (
    <>
      <button
        className="commonButton"
        onClick={() => (window.location.href = "/products/CreateProducts")}
        style={{ 
          marginLeft: "auto",
          fontSize: window.innerWidth <= 768 ? "14px" : "16px",
          padding: window.innerWidth <= 768 ? "8px 12px" : "10px 16px",
        }}
      >
        <IoBagAdd size={window.innerWidth <= 768 ? 18 : 20} color="#ffffff" style={{ marginRight: window.innerWidth <= 768 ? 6 : 8 }} />
        {window.innerWidth <= 480 ? "Add" : "Add Product"}
      </button>
      <div className="categories-container">
        <ReactSortable
          list={categoriesData}
          setList={handleRootSort}
          handle=".drag-handle"
          animation={200}
          ghostClass="sortable-ghost"
          chosenClass="sortable-chosen"
          dragClass="sortable-drag"
          style={{ listStyle: "none", padding: 0, margin: 0 }}
        >
          {categoriesData.map((cat) => (
            <div key={cat.id}>
              <CategoryItem
                category={cat}
                isChild={false}
                menuOpen={openMenuId === cat.name}
                menuId={cat.name}
                
                parentName={null}
                level={0}
                expandedMap={expandedMap}
                setExpandedMap={setExpandedMap}
                openMenuId={openMenuId}
                setOpenMenuId={setOpenMenuId}
                checkedMap={checkedMap}
                onToggleChange={handleToggleChange}
                onChildrenUpdate={(newChildren) => updateCategoryChildren(cat.id, newChildren)}
              />
            </div>
          ))}
        </ReactSortable>
      </div>
    </>
  );
};
export default Products;